import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import {
  BookOpen,
  Video,
  FileText,
  Lock,
  Search,
  ExternalLink,
  Play,
  Image as ImageIcon,
  File,
  X,
  Award,
  GraduationCap,
  LayoutGrid
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { getBeltName, getBeltBadgeColors } from '../shared/BeltBadge';
import { withMediaCacheKey } from '../../utils/mediaUrl';

import SectionNav from './SectionNav';

// Resource type sections for navigation
const RESOURCE_SECTIONS = [
  { id: 'all', label: 'All', icon: LayoutGrid },
  { id: 'video', label: 'Videos', icon: Video },
  { id: 'document', label: 'Documents', icon: FileText },
  { id: 'image', label: 'Images', icon: ImageIcon },
];

// Check if URL is a playable video
const isPlayableVideo = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.mov', '.webm', '.ogg', '.m4v'];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some(ext => lowerUrl.endsWith(ext));
};

// Check if URL is a previewable image
const isPreviewableImageUrl = (url) => {
  if (!url) return false;
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
  const lowerUrl = url.toLowerCase();
  return imageExtensions.some(ext => lowerUrl.endsWith(ext));
};

// ============================================
// Video Thumbnail Component
// ============================================

// Video placeholder component (shown when no thumbnail available)
const VideoPlaceholder = ({ className }) => (
  <div className={`${className} bg-gradient-to-br from-purple-100 to-purple-200 flex flex-col items-center justify-center relative`}>
    <Video className="h-12 w-12 text-purple-600" />
    <div className="mt-2 flex items-center gap-1">
      <Play className="h-4 w-4 text-purple-600" />
      <span className="text-xs font-medium text-purple-600">Tap to Play</span>
    </div>
    {/* Play button overlay */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="bg-black/40 rounded-full p-3">
        <Play className="h-6 w-6 text-white fill-white" />
      </div>
    </div>
  </div>
);

// Video thumbnail - uses server-generated thumbnail if available
const VideoThumbnail = ({ thumbnailUrl, alt, className }) => {
  // If we have a server-generated thumbnail, display it
  if (thumbnailUrl) {
    return (
      <div className={`${className} relative`}>
        <img
          src={thumbnailUrl}
          alt={alt}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover"
        />
        {/* Play button overlay - only visible on hover to reduce initial DOM load */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="bg-black/50 rounded-full p-3">
            <Play className="h-6 w-6 text-white fill-white" />
          </div>
        </div>
      </div>
    );
  }

  // No thumbnail available - show placeholder
  return <VideoPlaceholder className={className} />;
};

// ============================================
// Video Modal Component
// ============================================
const VideoModal = ({ isOpen, onClose, resource }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen && videoRef.current) {
      // Auto-play when modal opens
      videoRef.current.play().catch(() => {
        // Auto-play may be blocked by browser, that's okay
      });
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !resource) return null;

  // Use portal to render modal at document body level (escapes Card overflow constraints)
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
      onClick={onClose}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      <div
        className="relative w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with title and close button */}
        <div className="flex items-center justify-between mb-3 flex-shrink-0">
          <h3 className="text-white text-lg font-medium truncate pr-4">
            {resource.title}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-white hover:text-stone-300 transition-colors flex-shrink-0 bg-black/50 rounded-full"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Video player */}
        <div className="relative bg-black rounded-lg overflow-hidden flex-1 min-h-0">
          <video
            ref={videoRef}
            src={withMediaCacheKey(resource.url)}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-contain max-h-[75vh]"
          >
            Your browser does not support the video tag.
          </video>
        </div>

        {/* Description */}
        {resource.description && (
          <p className="text-stone-300 text-sm mt-3 flex-shrink-0">
            {resource.description}
          </p>
        )}
      </div>
    </div>,
    document.body
  );
};

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('memberToken') || localStorage.getItem('customerToken')}`,
  'Content-Type': 'application/json'
});

// Resource type icons
const TYPE_ICONS = {
  video: Video,
  document: FileText,
  image: ImageIcon,
};

// Get icon for resource type
const getResourceIcon = (type) => {
  return TYPE_ICONS[type] || File;
};

// Check if resource is an image that can be previewed
const isPreviewableImage = (resource) => {
  if (resource.resource_type === 'image') return true;
  if (resource.content_type?.startsWith('image/')) return true;
  if (isPreviewableImageUrl(resource.url)) return true;
  return false;
};

// Get color scheme based on resource type (Modern Dojo palette) - moved outside component
const getTypeColors = (resourceType) => {
  switch (resourceType) {
    case 'video':
      return { bg: 'bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20', text: 'text-[#9B1B30]', border: 'border-[#9B1B30]/20 dark:border-[#9B1B30]/30' };
    case 'document':
      return { bg: 'bg-[#C9A227]/10 dark:bg-[#C9A227]/20', text: 'text-[#C9A227]', border: 'border-[#C9A227]/20 dark:border-[#C9A227]/30' };
    case 'image':
      return { bg: 'bg-[#1A1814]/5 dark:bg-stone-700/50', text: 'text-[#1A1814] dark:text-stone-200', border: 'border-[#1A1814]/10 dark:border-stone-600' };
    default:
      return { bg: 'bg-[#FAF7F2] dark:bg-stone-800', text: 'text-[#6B6560] dark:text-stone-400', border: 'border-[#D6D3D1] dark:border-stone-600' };
  }
};

// ============================================
// Resource Card (Memoized)
// ============================================
const ResourceCard = memo(({ resource, isAccessible, onVideoClick }) => {
  const TypeIcon = getResourceIcon(resource.resource_type);
  const canPreviewImage = isPreviewableImage(resource);
  const canPlayVideo = isPlayableVideo(resource.url);

  const handleClick = useCallback(() => {
    if (!isAccessible || !resource.url) return;

    // If it's a playable video, open in modal instead of downloading
    if (canPlayVideo) {
      onVideoClick?.(resource);
      return;
    }

    // For other resources, open in new tab
    window.open(withMediaCacheKey(resource.url), '_blank');
  }, [isAccessible, canPlayVideo, onVideoClick, resource]);

  const colors = getTypeColors(resource.resource_type);

  return (
    <div
      onClick={isAccessible ? handleClick : undefined}
      className={`
        relative p-4 rounded-lg border transition-[border-color,box-shadow] duration-200 group
        ${isAccessible
          ? 'bg-white/90 dark:bg-stone-800/90 hover:shadow-lg hover:shadow-[#9B1B30]/10 hover:border-[#C9A227] cursor-pointer'
          : 'bg-[#FAF7F2]/50 dark:bg-stone-800/50 opacity-60 cursor-not-allowed'}
      `}
    >
      {/* Preview Area */}
      <div className="relative mb-3">
        {/* Video thumbnail */}
        {canPlayVideo && isAccessible ? (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-stone-100">
            <VideoThumbnail
              thumbnailUrl={resource.thumbnail_url}
              alt={resource.title}
              className="w-full h-full"
            />
          </div>
        ) : /* Image preview */
        canPreviewImage && isAccessible ? (
          <div className="relative aspect-video rounded-lg overflow-hidden bg-stone-100">
            <img
              src={resource.url}
              alt={resource.title}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className={`aspect-video rounded-lg ${colors.bg} flex flex-col items-center justify-center border ${colors.border}`}>
            <TypeIcon className={`h-12 w-12 ${isAccessible ? colors.text : 'text-stone-300'}`} />
            {resource.resource_type === 'video' && isAccessible && (
              <div className="mt-2 flex items-center gap-1">
                <Play className={`h-4 w-4 ${colors.text}`} />
                <span className={`text-xs font-medium ${colors.text}`}>Play Video</span>
              </div>
            )}
            {resource.filename && (
              <p className="text-xs text-stone-500 dark:text-stone-400 mt-2 px-2 text-center truncate max-w-full">
                {resource.filename}
              </p>
            )}
          </div>
        )}

        {/* Locked indicator */}
        {!isAccessible && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-lg">
            <div className="text-center">
              <Lock className="h-8 w-8 text-white mx-auto mb-1" />
              <p className="text-white text-xs font-medium">
                {resource.required_belt
                  ? `Requires ${getBeltName(resource.required_belt, 0)}`
                  : 'Locked'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div>
        <div className="flex items-start justify-between gap-2">
          <h4 className={`font-dm-sans font-medium ${isAccessible ? 'text-[#1A1814] dark:text-stone-100 group-hover:text-[#9B1B30]' : 'text-[#6B6560] dark:text-stone-500'} transition-colors`}>
            {resource.title}
          </h4>
          {isAccessible && (
            <ExternalLink className="h-4 w-4 text-[#C9A227] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </div>

        {resource.description && (
          <p className="text-sm text-[#6B6560] dark:text-stone-400 mt-1 line-clamp-2 font-dm-sans">
            {resource.description}
          </p>
        )}

        {/* Program and Belt Info (for accessible resources) */}
        {isAccessible && (resource.matching_program_name || resource.unlocked_at_belt) && (
          <div className="flex flex-wrap gap-2 mt-2">
            {resource.matching_program_name && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20 text-[#9B1B30] text-xs rounded-full font-dm-sans">
                <GraduationCap className="h-3 w-3" />
                {resource.matching_program_name}
              </span>
            )}
            {resource.unlocked_at_belt && (() => {
              const beltColors = getBeltBadgeColors(resource.unlocked_at_belt);
              return (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full font-dm-sans"
                  style={{ backgroundColor: beltColors.bg, color: beltColors.text }}
                >
                  <Award className="h-3 w-3" />
                  {getBeltName(resource.unlocked_at_belt, 0)}
                </span>
              );
            })()}
          </div>
        )}

        {/* Tags */}
        {resource.tags && resource.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {resource.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="px-2 py-0.5 bg-[#FAF7F2] dark:bg-stone-700 text-[#6B6560] dark:text-stone-300 text-xs rounded font-dm-sans"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Type badge */}
        <div className="flex items-center gap-1 mt-2 text-xs text-[#6B6560] dark:text-stone-400 font-dm-sans">
          <TypeIcon className="h-3 w-3" />
          <span className="capitalize">{resource.resource_type}</span>
        </div>
      </div>
    </div>
  );
});

// Set display name for debugging
ResourceCard.displayName = 'ResourceCard';

// ============================================
// Main Resource Library
// ============================================
const ResourceLibrary = ({
  student,
  activeSection: externalActiveSection,
  onSectionChange: externalOnSectionChange,
  hideNav = false,
  focusResourceId = null
}) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasFocused, setHasFocused] = useState(false);

  // Use external state if provided (controlled mode), otherwise use internal state
  const [internalActiveSection, setInternalActiveSection] = useState('all');
  const activeSection = externalActiveSection ?? internalActiveSection;
  const setActiveSection = externalOnSectionChange ?? setInternalActiveSection;

  const [videoModal, setVideoModal] = useState({ isOpen: false, resource: null });

  const handleVideoClick = useCallback((resource) => {
    setVideoModal({ isOpen: true, resource });
  }, []);

  const closeVideoModal = useCallback(() => {
    setVideoModal({ isOpen: false, resource: null });
  }, []);

  const fetchResources = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_BASE}/api/customer/students/${student.id}/resources`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setResources(data);
      }
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  }, [student?.id]);

  useEffect(() => {
    if (student?.id) {
      fetchResources();
    }
  }, [student?.id, fetchResources]);

  // Auto-open focused resource after loading (from deep link)
  useEffect(() => {
    if (!focusResourceId || loading || hasFocused || resources.length === 0) return;
    const target = resources.find(r => r.id === focusResourceId);
    if (!target || !target.is_accessible) return;

    setHasFocused(true);
    if (isPlayableVideo(target.url)) {
      setVideoModal({ isOpen: true, resource: target });
    } else if (target.url) {
      window.open(withMediaCacheKey(target.url), '_blank');
    }
  }, [focusResourceId, loading, hasFocused, resources]);

  // Memoized single-pass filtering and stats calculation
  const { accessibleResources, lockedResources, stats } = useMemo(() => {
    const typeFilter = activeSection === 'all' ? '' : activeSection;
    const searchLower = searchTerm.toLowerCase();

    // Single-pass computation
    const accessible = [];
    const locked = [];
    let totalAccessible = 0;
    let totalLocked = 0;
    let videoCount = 0;
    let documentCount = 0;
    let imageCount = 0;

    for (const resource of resources) {
      // Count stats (always)
      if (resource.is_accessible) totalAccessible++;
      else totalLocked++;

      if (resource.resource_type === 'video') videoCount++;
      else if (resource.resource_type === 'document') documentCount++;
      else if (resource.resource_type === 'image') imageCount++;

      // Filter for display
      const matchesSearch = !searchTerm ||
        resource.title.toLowerCase().includes(searchLower) ||
        resource.description?.toLowerCase().includes(searchLower) ||
        resource.tags?.some(t => t.toLowerCase().includes(searchLower));

      const matchesType = !typeFilter || resource.resource_type === typeFilter;

      if (matchesSearch && matchesType) {
        if (resource.is_accessible) {
          accessible.push(resource);
        } else {
          locked.push(resource);
        }
      }
    }

    return {
      accessibleResources: accessible,
      lockedResources: locked,
      stats: {
        totalAccessible,
        totalLocked,
        videoCount,
        documentCount,
        imageCount
      }
    };
  }, [resources, activeSection, searchTerm]);

  if (!student) {
    return null;
  }

  if (loading) {
    return (
      <Card className="member-card">
        <CardContent className="py-8 text-center text-stone-500 dark:text-stone-400">
          Loading resources...
        </CardContent>
      </Card>
    );
  }

  return (
    <SectionNav
      sections={RESOURCE_SECTIONS}
      storageKey="resources_section"
      studentId={student.id}
      activeSection={activeSection}
      onSectionChange={setActiveSection}
      hideNav={hideNav}
    >
      <Card className="member-card dojo-animate">
        <CardHeader className="pb-2 border-b border-stone-200 dark:border-stone-700 bg-gradient-to-r from-transparent to-stone-50 dark:to-stone-800/50">
          <CardTitle className="text-lg flex items-center gap-2 text-stone-800 dark:text-stone-100 font-outfit">
            <BookOpen className="h-5 w-5 text-[#9B1B30]" />
            Training Resources
          </CardTitle>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-stone-500 dark:text-stone-400 mt-2 font-dm-sans">
            <span className="font-jetbrains font-bold text-[#9B1B30]">{stats.totalAccessible}</span> available
            {stats.totalLocked > 0 && (
              <span className="flex items-center gap-1">
                <Lock className="h-3 w-3" />
                <span className="font-jetbrains font-bold text-[#C9A227]">{stats.totalLocked}</span> locked
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Count Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {/* Total Resources */}
            <button
              onClick={() => { setActiveSection('all'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                activeSection === 'all'
                  ? 'bg-stone-100 dark:bg-stone-700 ring-2 ring-stone-500'
                  : 'bg-stone-50 dark:bg-stone-800/50 hover:bg-stone-100 dark:hover:bg-stone-700'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <LayoutGrid className="h-5 w-5 text-stone-600 dark:text-stone-300" />
                <div className="text-2xl font-jetbrains font-bold text-stone-800 dark:text-stone-100">
                  {resources.length}
                </div>
                <div className="text-xs text-stone-600 dark:text-stone-300 font-dm-sans">
                  Total
                </div>
              </div>
            </button>

            {/* Available Resources */}
            <button
              onClick={() => { setActiveSection('all'); setSearchTerm(''); }}
              className="p-3 rounded-lg transition-all bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 hover:shadow-md"
            >
              <div className="flex flex-col items-center gap-1">
                <BookOpen className="h-5 w-5 text-green-600 dark:text-green-400" />
                <div className="text-2xl font-jetbrains font-bold text-green-800 dark:text-green-300">
                  {stats.totalAccessible}
                </div>
                <div className="text-xs text-green-600 dark:text-green-400 font-dm-sans">
                  Available
                </div>
              </div>
            </button>

            {/* Locked Resources */}
            <button
              onClick={() => { setActiveSection('all'); setSearchTerm(''); }}
              className="p-3 rounded-lg transition-all bg-[#C9A227]/10 dark:bg-[#C9A227]/20 hover:bg-[#C9A227]/20 dark:hover:bg-[#C9A227]/30 hover:shadow-md"
            >
              <div className="flex flex-col items-center gap-1">
                <Lock className="h-5 w-5 text-[#C9A227]" />
                <div className="text-2xl font-jetbrains font-bold text-[#C9A227]">
                  {stats.totalLocked}
                </div>
                <div className="text-xs text-[#C9A227] font-dm-sans">
                  Locked
                </div>
              </div>
            </button>

            {/* Videos */}
            <button
              onClick={() => { setActiveSection('video'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                activeSection === 'video'
                  ? 'bg-[#9B1B30]/20 dark:bg-[#9B1B30]/40 ring-2 ring-[#9B1B30]'
                  : 'bg-[#9B1B30]/10 dark:bg-[#9B1B30]/20 hover:bg-[#9B1B30]/20 dark:hover:bg-[#9B1B30]/30'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <Video className="h-5 w-5 text-[#9B1B30]" />
                <div className="text-2xl font-jetbrains font-bold text-[#9B1B30]">
                  {stats.videoCount}
                </div>
                <div className="text-xs text-[#9B1B30] font-dm-sans">
                  Videos
                </div>
              </div>
            </button>

            {/* Documents */}
            <button
              onClick={() => { setActiveSection('document'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                activeSection === 'document'
                  ? 'bg-[#C9A227]/20 dark:bg-[#C9A227]/40 ring-2 ring-[#C9A227]'
                  : 'bg-[#C9A227]/10 dark:bg-[#C9A227]/20 hover:bg-[#C9A227]/20 dark:hover:bg-[#C9A227]/30'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <FileText className="h-5 w-5 text-[#C9A227]" />
                <div className="text-2xl font-jetbrains font-bold text-[#C9A227]">
                  {stats.documentCount}
                </div>
                <div className="text-xs text-[#C9A227] font-dm-sans">
                  Documents
                </div>
              </div>
            </button>

            {/* Images */}
            <button
              onClick={() => { setActiveSection('image'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                activeSection === 'image'
                  ? 'bg-stone-700/20 dark:bg-stone-600/40 ring-2 ring-stone-700 dark:ring-stone-500'
                  : 'bg-stone-700/10 dark:bg-stone-600/20 hover:bg-stone-700/20 dark:hover:bg-stone-600/30'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <ImageIcon className="h-5 w-5 text-stone-700 dark:text-stone-300" />
                <div className="text-2xl font-jetbrains font-bold text-stone-700 dark:text-stone-300">
                  {stats.imageCount}
                </div>
                <div className="text-xs text-stone-700 dark:text-stone-300 font-dm-sans">
                  Images
                </div>
              </div>
            </button>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#9B1B30]" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search resources..."
                className="pl-9 bg-white dark:bg-stone-800 border-stone-300 dark:border-stone-600 focus:border-[#9B1B30] focus:ring-[#9B1B30]/20 font-dm-sans text-stone-800 dark:text-stone-100"
              />
            </div>
          </div>

          {accessibleResources.length === 0 && lockedResources.length === 0 ? (
            <div className="text-center py-8 text-stone-500 dark:text-stone-400 font-dm-sans">
              {searchTerm || activeSection !== 'all'
                ? 'No resources match your search.'
                : 'No resources available yet.'}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Accessible Resources */}
              {accessibleResources.length > 0 && (
                <div>
                  <h4 className="text-sm font-outfit font-semibold text-stone-800 dark:text-stone-100 mb-3">
                    Available Resources <span className="font-jetbrains text-[#9B1B30]">({accessibleResources.length})</span>
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ contentVisibility: 'auto' }}>
                    {accessibleResources.map(resource => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        isAccessible={true}
                        onVideoClick={handleVideoClick}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Locked Resources */}
              {lockedResources.length > 0 && (
                <div>
                  <h4 className="text-sm font-outfit font-semibold text-[#1A1814] dark:text-stone-100 mb-3 flex items-center gap-2">
                    <Lock className="h-4 w-4 text-[#C9A227]" />
                    Unlock with Progress <span className="font-jetbrains text-[#C9A227]">({lockedResources.length})</span>
                  </h4>
                  <p className="text-xs text-[#6B6560] dark:text-stone-400 mb-3 font-dm-sans">
                    These resources unlock as you advance in belt rank or program enrollment.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ contentVisibility: 'auto' }}>
                    {lockedResources.map(resource => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        isAccessible={false}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>

        {/* Video Modal */}
        <VideoModal
          isOpen={videoModal.isOpen}
          onClose={closeVideoModal}
          resource={videoModal.resource}
        />
      </Card>
    </SectionNav>
  );
};

export default ResourceLibrary;
